import { getUniquePrimitiveItemsFromArray } from 'utils/helpers';
import {
  REGEX_COMMENT_USER_MENTION,
  REGEX_MENTION,
  REGEX_NETWORK_MENTION,
} from '@kontentino/kontentino-constants/Regex';
import { stripMentionMarkup } from 'app/modules/textEditor/utils/mentionCleaner';

type MentionableUser = {
  id: string | number;
  name: string;
  role?: string;
  link?: string;
  /**
   * Explicit override for the inserted mention's label (and visible text),
   * distinct from `name`. Instagram and X resolve `@handle` themselves at
   * publish time, so the composer must insert the handle there, not the
   * display name `name` carries. Every existing caller leaves this unset and
   * keeps getting `name`, unchanged.
   */
  label?: string;
};

const TextEditorConvertor = {
  validateAndCleanMentions(text: string, targetPlatformType?: number): string {
    return stripMentionMarkup(text);
  },
  getMentionId(mention: string) {
    const meta = mention.match(/\(([^)]+)\)/)?.[1] ?? '';

    const urnPrefix = 'urn:li:person:';
    if (meta.startsWith(urnPrefix)) {
      const afterPrefix = meta.slice(urnPrefix.length);
      const id = afterPrefix.split('+')[0];
      return id || undefined;
    }

    if (meta.startsWith('company:')) {
      const id = meta.split(':')[1];
      return id || undefined;
    }

    const numeric = mention.match(/(\d+)/);
    return numeric?.[1];
  },
  textToHtml(
    text = '',
    options?: {
      mentionableUsers?: MentionableUser[];
    },
  ) {
    function getMentionsFromText(text: string) {
      const rawProfileMentions = getUniquePrimitiveItemsFromArray([
        ...(text.match(new RegExp(REGEX_NETWORK_MENTION, 'gi'))?.map(String) ??
          []),
        ...(text.match(new RegExp(REGEX_MENTION, 'gi'))?.map(String) ?? []),
      ]);

      const rawUserMentions = getUniquePrimitiveItemsFromArray(
        text.match(new RegExp(REGEX_COMMENT_USER_MENTION, 'gi'))?.map(String) ??
          [],
      );

      const mentions: Record<string, MentionableUser> = {};

      rawProfileMentions.forEach((rawMention) => {
        const id = TextEditorConvertor.getMentionId(rawMention);

        const name = rawMention
          .match(new RegExp(/\[\D+]/, 'gi'))
          ?.toString()
          ?.replace(/([\][])/g, '');

        const meta = rawMention.match(/\(([^)]+)\)/)?.[1] ?? '';
        let role: string | undefined = undefined;
        let link: string | undefined = undefined;

        if (meta.startsWith('urn:li:person:')) {
          role = 'user';
          const plusIndex = meta.indexOf('+');
          if (plusIndex !== -1 && plusIndex + 1 < meta.length) {
            link = meta.substring(plusIndex + 1);
          }
        } else if (meta.startsWith('company:')) {
          role = 'company';
        }

        if (id && name) {
          mentions[rawMention] = { id, name, role, link };
        }
      });

      rawUserMentions.forEach((rawMention) => {
        const id = TextEditorConvertor.getMentionId(rawMention);

        const user = options?.mentionableUsers?.find(
          (user) => `${user.id}` === id,
        );

        if (id && user) {
          mentions[rawMention] = { id, name: user.name, role: user.role };
        }
      });

      return Object.entries(mentions);
    }

    const mentions = getMentionsFromText(text);

    // Mentions sit behind placeholders while URLs are linkified, so a URL
    // inside a mention's name or link can never be rewritten into markup. The
    // marker opens with `<`, where the URL pattern stops, and is lengthened
    // until it cannot occur in the text itself.
    let placeholderPrefix = '<!--mention:';
    while (text.includes(placeholderPrefix)) {
      placeholderPrefix += '-';
    }
    const mentionPlaceholder = (index: number) =>
      `${placeholderPrefix}${index}-->`;

    function hideMentions(text: string) {
      return mentions.reduce(
        (html, [mentionText], index) =>
          html.replaceAll(mentionText, () => mentionPlaceholder(index)),
        text,
      );
    }

    // A callback, not a replacement string: a name holding `$&` or `$$` would
    // otherwise be read as a substitution pattern.
    function convertMentionsToHtml(text: string) {
      return text.replace(
        new RegExp(`${placeholderPrefix}(\\d+)-->`, 'g'),
        (placeholder: string, index: string) => {
          const mention = mentions[Number(index)]?.[1];

          return mention
            ? TextEditorConvertor.suggestionToMentionTag(mention)
            : placeholder;
        },
      );
    }

    function convertLineBreaksToBrTag(text: string) {
      const LINE_BREAK = /(\r\n|\n|\r)/gm;

      return `${text.replace(LINE_BREAK, '<br>')}`;
    }

    function convertUrlsToLinks(text: string) {
      return text.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1">$1</a>');
    }

    const processes = [
      (val: string) => hideMentions(val),
      (val: string) => convertUrlsToLinks(val),
      (val: string) => convertMentionsToHtml(val),
      (val: string) => convertLineBreaksToBrTag(val),
    ];

    let result = text;

    processes.forEach((process) => (result = process(result)));

    return result;
  },
  suggestionToMentionTag(mention: MentionableUser) {
    // Names come from the networks verbatim. Unescaped, an apostrophe ends the
    // single-quoted attribute early — "McDonald's" became the label "McDonald",
    // and re-saving the post then stored the cut name.
    function escapeHtml(value: string | number) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    function recordToHtmlAttributes(
      record: Record<string, string | number | undefined>,
    ) {
      return Object.entries(record)
        .filter(([, value]) => !!value)
        .map(
          ([key, value]) => `${key}='${escapeHtml(value as string | number)}'`,
        )
        .join(' ');
    }

    const label = mention.label ?? mention.name;

    return `<span data-type='mention' class='mention' ${recordToHtmlAttributes({
      'data-id': mention.id,
      'data-label': label,
      'data-role': mention.role,
      'data-link': mention.link,
    })} contenteditable='false'>${escapeHtml(label)}</span>`;
  },
};

export default TextEditorConvertor;
